import { Timer } from 'lucide-react';
import * as React from 'react';

import {
  CHECKOUT_SECTION_CARD,
  COUNTDOWN_TICK_MS,
  seatHeldLine,
  secondsLeftOnHold,
} from './constants';
import type { SeatHold } from './types';

/**
 * **The visible countdown** (`MEM-020` AC-2, `EVT-022` AC-2's third clause).
 *
 * `EVT-022` built the whole server half of AC-2 — the hold starts at the instant checkout does, it
 * lasts fifteen minutes from one constant, a reload hands back the hold that exists rather than a
 * fresh fifteen, and the member is served `expiresAt` and `secondsRemaining` — and then recorded
 * the criterion as **not passing**, because *"visible"* had no surface. Its `blocked_reason` says
 * so in as many words: *"unblocks with MEM-020, which builds the checkout screen and tags AC-2
 * from there."* This is that surface.
 *
 * ## What it does each second, and what it does not
 *
 * It ticks **locally**. It does not poll: a request per second per member at checkout is a request
 * per second per member at checkout, and there is nothing to learn from the server between ticks —
 * the expiry does not move.
 *
 * The arithmetic is `secondsLeftOnHold`, and it takes the **smaller** of two readings: the served
 * seconds minus wall time elapsed since the response, and the served expiry minus the device
 * clock. Its header has the full argument; the short version is that the first is wrong for a tab
 * that was asleep and the second is wrong for a device whose clock is wrong, and taking the
 * minimum makes the countdown able to under-state the time left and unable to over-state it. A
 * member told they have less time than they do goes faster; one told they have more loses a seat
 * mid-form.
 *
 * `Date.now()` is read at each tick rather than captured once, so a phone that slept through ten
 * minutes catches up on its first frame awake rather than resuming where it stopped.
 *
 * ## At zero
 *
 * `onExpired` fires **once**, and the component stops. It does not itself decide what an expired
 * hold means — the screen does, by rendering the served refusal and an action that starts again —
 * because "the seat went back" is not the same claim as "the event is full", and only the server
 * knows which.
 */
export function SeatHoldCountdown({
  hold,
  onExpired,
}: {
  readonly hold: SeatHold;
  readonly onExpired?: () => void;
}): React.ReactElement {
  /**
   * The figures this countdown is counting from, and the instant they arrived.
   *
   * Held in state and reset whenever the served hold changes, rather than captured once at mount:
   * re-opening checkout hands back a hold with a new `secondsRemaining`, and an anchor taken at
   * mount would keep counting from the first one while the seat it describes had been renewed.
   */
  const [anchor, setAnchor] = React.useState(() => ({
    receivedAt: Date.now(),
    servedSecondsRemaining: hold.secondsRemaining,
    expiresAt: hold.expiresAt,
  }));
  React.useEffect(() => {
    setAnchor({
      receivedAt: Date.now(),
      servedSecondsRemaining: hold.secondsRemaining,
      expiresAt: hold.expiresAt,
    });
  }, [hold.secondsRemaining, hold.expiresAt]);

  const remainingNow = React.useCallback(
    () =>
      secondsLeftOnHold({
        servedSecondsRemaining: anchor.servedSecondsRemaining,
        // Read at each call rather than captured, so a phone that slept through ten minutes catches
        // up on its first frame awake instead of resuming where its timers stopped.
        elapsedMs: Date.now() - anchor.receivedAt,
        expiresAt: anchor.expiresAt,
        now: Date.now(),
      }),
    [anchor],
  );

  const [secondsRemaining, setSecondsRemaining] = React.useState(remainingNow);

  React.useEffect(() => {
    setSecondsRemaining(remainingNow());
    const timer = setInterval(() => {
      setSecondsRemaining(remainingNow());
    }, COUNTDOWN_TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [remainingNow]);

  const expired = secondsRemaining <= 0;
  const notified = React.useRef(false);
  React.useEffect(() => {
    if (!expired || notified.current) return;
    notified.current = true;
    onExpired?.();
  }, [expired, onExpired]);

  return (
    /* **A white card directly above the reserve action, in larger type** — TW 44132872
       (https://ten243.teamwork.com/app/tasks/44132872), approved by Sumit on 2026-09-18. It was
       §3.1's small brand chip at the top of the column, which read as a note about the page; the
       clock on this member's seat is the last thing they should see before they commit, so it now
       stands in the house card (`CHECKOUT_SECTION_CARD`) where the refund terms used to be, and the
       screen places it immediately before the action. */
    <div className={`${CHECKOUT_SECTION_CARD} flex items-center gap-3`}>
      <Timer className="h-6 w-6 shrink-0 text-brand" aria-hidden />
      <p
        /*
         * `role="timer"` with `aria-live="off"`: the value is announced when a screen-reader user
         * asks for it, and **not** every second, which would make the page unusable
         * (`NFR accessibility#wcag-aa`). The rendered text is a whole sentence rather than a bare
         * `14:32`, so the announcement says what the number is about.
         */
        role="timer"
        aria-live="off"
        className="font-brand text-lg font-bold tabular-nums text-foreground"
      >
        {seatHeldLine(secondsRemaining)}
      </p>
    </div>
  );
}
